import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { translate } from 'components/translation/translation';
import { IcoLens } from 'icone';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import AG_GRID_LOCALE_IT from 'components/ag-grid/locale-it';
import PeriodicReportingStatusCell from './PeriodicReportingStatusCell';
import PeriodicReportingInfoCell from './PeriodicReportingInfoCell';
import { columnCentered } from 'lib/helpers/agGridHelpers';
import { ExternalCostType } from './PeriodicReportingExternalCosts';
import { getChecklistOptionsByEntityType, getInvoicesToAnalyzeByProjectIdAndStatementId } from 'lib/api_ts/periodicReportingApi';
import { useLocation } from 'react-router-dom-v5-compat';
import { PeriodicReportingType } from '../PeriodicReporting';
import { formatToDecimals } from 'lib/helpers/formatting';
import CustomTooltip from 'components/ag-grid/CustomTooltip';

interface PeriodicReportingRecordAnalysisProps {
	data: PeriodicReportingType;
	isCoordinator: boolean;
	isEnabledToView: boolean;
	state: 'DRAFT' | 'VALIDATED';
	selectedIds: number[];
	setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
	invoiceId: string;
	setInvoiceId: React.Dispatch<React.SetStateAction<string>>;
	entity: string;
	setEntity: React.Dispatch<React.SetStateAction<string>>;
	rc: string[];
	setRc: React.Dispatch<React.SetStateAction<string[]>>;
}

const PeriodicReportingRecordAnalysis: React.FC<PeriodicReportingRecordAnalysisProps> = ({
	data,
	isCoordinator,
	isEnabledToView,
	state,
	selectedIds,
	setSelectedIds,
	entity,
	invoiceId,
	rc,
	setEntity,
	setInvoiceId,
	setRc,
}) => {
	const localeText = AG_GRID_LOCALE_IT;

	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	const entityOptions = [
		{ value: 'invoices', label: 'Fattura' },
		{ value: 'tenders  ', label: 'Procedura' },
		{ value: 'contracts', label: 'Attingimento' },
		{ value: 'frameworkcontracts', label: 'Contratto Aperto' },
		{ value: 'invoicepayments', label: 'Pagamento' },
		{ value: 'otplants', label: 'DNSH' },
	];

	const [checklistOptions, setChecklistOptions] = useState<{ label: string; value: string }[]>([]);

	const isSearchedRef = useRef(false);

	useEffect(() => {
		(async () => {
			if (!entity) return;
			try {
				const checklistOptionsFromApi = await getChecklistOptionsByEntityType(projectId, data.id, entity);
				setChecklistOptions(
					checklistOptionsFromApi.map((checklistOption) => ({
						label: `${checklistOption.code} ${checklistOption.title}`,
						value: checklistOption.code,
					})),
				);
			} catch (error) {
				console.error('error', error);
			}
		})();
	}, [projectId, data.id, entity, setRc]);

	useEffect(() => {
		if (!entity) setRc([]);
	}, [entity, setRc]);

	const [rowData, setRowData] = useState<ExternalCostType[]>([]);

	const [isLoading, setIsLoading] = useState(false);

	const fetchData = async () => {
		setIsLoading(true);
		isSearchedRef.current = true;
		try {
			const invoicesToAnalyzeFromApi = await getInvoicesToAnalyzeByProjectIdAndStatementId(projectId, data.id, {
				rpCode: invoiceId,
				type: entity,
				rc,
			});
			const formattedData = invoicesToAnalyzeFromApi.map((invoice) => {
				return {
					id: invoice.id,
					beneficiaryName: invoice.beneficiaryName,
					number: invoice.number,
					rpCode: invoice.rpCode,
					supplierName: invoice.supplierName,
					expenseItemName: invoice.expenseItemName,
					noticeExpenseItemName: invoice.noticeExpenseItemName,
					amount: invoice.amount,
					toSettle: invoice.toSettle,
				};
			});
			setRowData(formattedData);
			setIsLoading(false);
		} catch (error) {
			console.error('error', error);
		}
	};

	const columnDefs = [
		...(isCoordinator && state === 'DRAFT'
			? [
				{
					headerName: '',
					//field: "id",
					// hide: isValidated ? true : false,
					maxWidth: 50,
					resizable: false,
					headerCheckboxSelection: true,
					// checkboxSelection: isValidated ? false : true,
					checkboxSelection: true,
					// cellStyle: () => {
					//   //per disabilitare il click sulla riga in loading
					//   return loading || pickLoading
					//     ? { pointerEvents: "none", opacity: "0.4" }
					//     : null;
					// },
					// headerComponent: CustomHeader,
					// headerComponentParams: {
					//   entityData: invoices,
					//   entityType: "invoices",
					//   //    setOriginItemList: setOriginCheckedInvoices,
					// },
				},
			]
			: []),
		{
			headerName: 'Beneficiario',
			field: 'beneficiaryName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Nr. Fattura',
			field: 'number',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'ID Fattura',
			field: 'rpCode',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Fornitore',
			field: 'supplierName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Voce di spesa',
			// field: 'name',
			field: 'noticeExpenseItemName',
			filter: 'agTextColumnFilter',
			tooltipField: 'name',
			tooltipComponent: CustomTooltip,

			//minWidth: 100,
		},
		{
			headerName: 'Importo rendicontabile €',
			field: 'amount',
			filter: 'agTextColumnFilter',
			type: 'rightAligned',
			resizable: true,
			valueGetter: (params: { data: { amount: number } }) => {
				return params.data.amount ? formatToDecimals(params.data.amount) : 0;
			},
		},
		{
			headerName: 'Status',
			field: 'toSettle',
			cellRenderer: PeriodicReportingStatusCell,
			minWidth: 90,
			maxWidth: 90,
			...columnCentered(),
		},
		{
			headerName: 'Info',
			field: 'toSettle',
			cellRenderer: PeriodicReportingInfoCell,
			cellRendererParams: {
				selectedIds,
				statementId: data.id,
				fetchData,
				isCoordinator,
				isEnabledToView,
			},
			minWidth: 90,
			maxWidth: 90,
			...columnCentered(),
		},
	];

	const defaultColDef = {
		resizable: false,
		wrapText: true,
		autoHeight: true,
		flex: 1,
		suppressMovable: true,
		cellStyle: {
			padding: '0 1rem',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
	};

	const handleData = (params: {
		api: {
			forEachNode: (
				arg0: (node: { setSelected: (arg0: boolean, arg1: boolean, arg2: boolean) => void; data: { checked: number } }) => void,
			) => void;
		};
	}) => {
		params.api.forEachNode((node) => {
			node.setSelected(node.data.checked === 1, false, true);
		});
	};

	return (
		<>
			<Row>
				<Form.Group as={Col} md={3} style={{ paddingInline: '20px' }}>
					<Form.Label className="mb-0">ID Fattura</Form.Label>
					<Form.Control
						type="text"
						placeholder={translate('common.search', 'Cerca') + '...'}
						value={invoiceId}
						onChange={(e) => setInvoiceId(e.target.value)}
					/>
				</Form.Group>
				<Form.Group as={Col} md={3} style={{ paddingInline: '20px', paddingLeft: 0 }}>
					<Form.Label className="mb-0">Entità</Form.Label>

					<Select
						components={{
							IndicatorSeparator: () => null,
						}}
						className="react-select-container"
						classNamePrefix="react-select"
						name="type"
						placeholder={translate('common.placeholder.select', 'Seleziona...')}
						options={entityOptions}
						value={entityOptions.find((option) => option.value === entity)}
						onChange={(event) => setEntity(event?.value || '')}
						isClearable
					/>
				</Form.Group>
				<Form.Group as={Col} md={3} style={{ paddingInline: '20px', paddingLeft: 0 }}>
					<Form.Label className="mb-0">Checklist</Form.Label>
					<Select
						components={{
							IndicatorSeparator: () => null,
						}}
						className="react-select-container"
						classNamePrefix="react-select"
						name="type"
						placeholder={translate('common.placeholder.select', 'Seleziona...')}
						isDisabled={!entity}
						options={checklistOptions}
						value={checklistOptions.filter((option) => rc.includes(option.value))}
						onChange={(event) => {
							setRc(event ? event.map((option) => option.value) : []);
						}}
						isClearable
						isMulti
					/>
				</Form.Group>

				<Form.Group as={Col} md={3} style={{ paddingInline: '20px', paddingLeft: 0 }}>
					<Form.Label className="mb-0"></Form.Label>
					<Button onClick={fetchData} className="d-block" style={{ padding: 0, height: '2.235rem', width: '2.211rem' }}>
						{isLoading ? (
							<Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
						) : (
							<IcoLens className="ico-small edit" />
						)}
					</Button>
				</Form.Group>
			</Row>
			{isSearchedRef.current ? (
				rowData.length > 0 ? (
					<div className="ag-theme-alpine list-table relative mt-4">
						<AgGridReact
							rowData={rowData}
							columnDefs={[...columnDefs]}
							defaultColDef={defaultColDef}
							domLayout="autoHeight"
							tooltipShowDelay={0}
							tooltipHideDelay={2000}
							tooltipMouseTrack={true}
							headerHeight={50}
							rowHeight={50}
							suppressCellFocus={true}
							localeText={localeText}
							rowSelection="multiple"
							rowMultiSelectWithClick={true}
							onFirstDataRendered={handleData}
							onGridReady={handleData}
							suppressRowClickSelection={true}
							onSelectionChanged={(event) => {
								setSelectedIds(event.api.getSelectedRows().map((row: ExternalCostType) => row.id));
							}}
						/>
					</div>
				) : (
					<Row className="text-center my-5">
						<span>Non sono presenti record per i filtri indicati</span>
					</Row>
				)
			) : null}
		</>
	);
};

export default PeriodicReportingRecordAnalysis;
